import { DevTool } from '@hookform/devtools'
import { yupResolver } from '@hookform/resolvers/yup'
import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { DIGIT } from './KeyboardButton'
import type { ReactElement } from 'react'
import type { IAppManagementByDeviceType } from '~/types/application-management'
import type { IKeyBinding } from '~/types/user-preference'

import KeyboardBindingComponent from './KeyboardBindingComponent'
import KeyboardButton from './KeyboardButton'

import CustomAppOption from '../SelectOptions/CustomAppOption'

import Banner from '~/components/Banner'
import Button from '~/components/Buttons/Button'
import SelectInput from '~/components/Inputs/ReactHookForm/SelectInput'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import { FORM_ERRORS } from '~/const'
import { FASTLY_PATH } from '~/const/asset'
import { bindingOptions, needAppKeyView } from '~/const/settings'
import { useAppsManagementListQuery } from '~/queries/application-management'
import { usersKeys, useUpdateUserPreferencesMutation, useUserPreferencesQuery } from '~/queries/users'
import { splitByDeviceType } from '~/utils/application'
import { resize } from '~/utils/media'
import { reactSelectMenuPositionDef } from '~/utils/options'
import { displayToast } from '~/utils/toast'
import { RequiredYupOption } from '~/utils/yup'

import './styles.scss'

const MODAL_ID = 'setting-modal'

type FormValues = {
  application: { label: string; value: string } | null
  binding: IOption
  view_path: IOption
}

const validationSchema = Yup.object().shape({
  application: Yup.object({
    label: Yup.string().required(),
    value: Yup.string().required(),
  })
    .when('view_path', {
      is: (viewPath: IOption) => viewPath && !!needAppKeyView.includes(viewPath.value),
      then: (schema) => schema.required(FORM_ERRORS.REQUIRED),
    })
    .nullable(),
  binding: RequiredYupOption.required(FORM_ERRORS.REQUIRED),
  view_path: RequiredYupOption.required(FORM_ERRORS.REQUIRED),
})

interface ModalFormProps {
  onClose: VoidFunction
  viewPathOptions: IOption[]
}

const SettingForm = ({ onClose, viewPathOptions }: ModalFormProps): ReactElement => {
  /** Local state */

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
    watch,
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
  })

  const [warningBannerOpen, setWarningBannerOpen] = useState(false)

  const watchViewPath = watch('view_path')

  const watchedBinding = watch('binding')

  const needAppKeyDeviceType = watchViewPath && needAppKeyView.includes(watchViewPath.value)

  /** Queries & Mutations */

  const { data: apps } = useAppsManagementListQuery()

  const { data: userPreferences } = useUserPreferencesQuery()

  const { mutateAsync: updateUserPreferencesMutation } = useUpdateUserPreferencesMutation()

  const queryClient = useQueryClient()

  const keyBindings = useMemo(() => userPreferences?.shortcuts?.key_func || [], [userPreferences])

  const applicationOptions =
    apps
      ?.reduce(splitByDeviceType(), [] as IAppManagementByDeviceType[])
      .map(({ app_key, default_display_name, device_type, icon_file_id }) => ({
        device: device_type,
        label: default_display_name,
        url: icon_file_id ? resize(`${FASTLY_PATH}${icon_file_id}`, 32, 32) : undefined,
        value: JSON.stringify({
          app_key,
          device_type,
        }),
      }))
      .sort(({ label: aName }, { label: bName }) => aName.localeCompare(bName)) || []

  /** Effects */

  useEffect(() => {
    if (watchedBinding && watchedBinding.value) {
      setWarningBannerOpen(keyBindings?.some(({ key_value }) => key_value === watchedBinding.value))
    }
  }, [keyBindings, watchedBinding])

  useEffect(() => {
    if (watchViewPath) setValue('application', null)
  }, [setValue, watchViewPath])

  /** Handlers */

  const handleFormSubmit = async (values: FormValues) => {
    const { application, binding, view_path } = values
    const { app_key, device_type } = JSON.parse(application?.value || '{}')

    const newBinding: IKeyBinding = {
      app_key,
      device_type,
      key_value: binding.value,
      view_path: app_key && device_type ? `${view_path.value}?${app_key}:${device_type}` : view_path.value,
    }

    const isKeyAlreadyAssigned = keyBindings.findIndex(({ key_value }) => key_value === newBinding.key_value)
    const tempKeyBindings = [...keyBindings]

    try {
      if (userPreferences) {
        if (isKeyAlreadyAssigned !== -1) {
          tempKeyBindings.splice(isKeyAlreadyAssigned, 1, newBinding)

          await updateUserPreferencesMutation({
            ...userPreferences,
            shortcuts: {
              key_func: tempKeyBindings,
            },
          })
        } else {
          await updateUserPreferencesMutation({
            ...userPreferences,
            shortcuts: {
              key_func: userPreferences.shortcuts?.key_func
                ? [...userPreferences.shortcuts.key_func, newBinding]
                : [newBinding],
            },
          })
        }

        await queryClient.invalidateQueries({ queryKey: usersKeys.preferences() })
        displayToast(`Binding successfully ${isKeyAlreadyAssigned ? 'updated' : 'created'}`, 'success')
        setWarningBannerOpen(false)
        reset()
      }
    } catch (e) {
      captureException(e)
      displayToast('Binding could not be saved', 'error')
    }
  }

  const handleDeleteClick = (key: string) => async () => {
    try {
      if (userPreferences) {
        await updateUserPreferencesMutation({
          ...userPreferences,
          shortcuts: { key_func: keyBindings.filter(({ key_value }) => key_value !== key) },
        })
      }
      await queryClient.invalidateQueries({ queryKey: usersKeys.preferences() })
      displayToast('Binding successfully deleted', 'success')
    } catch (e) {
      captureException(e)
      displayToast('Binding could not be deleted', 'error')
    }
  }

  /** Render */

  return (
    <>
      <ModalBody className="setting-modal__body">
        {/* LeftMenu Component to add here when needed */}
        <div className="setting-modal__body__content">
          <h2 className="setting-modal__body__content__title">Keyboard shortcuts</h2>
          {keyBindings && keyBindings.length > 0 ? (
            <>
              <h4>Already binded ({keyBindings ? 9 - keyBindings.length : 9} left)</h4>
              <div className="setting-modal__body__content__binding-mapping">
                {keyBindings.map(({ app_key, device_type, key_value, view_path }) => (
                  <KeyboardBindingComponent
                    appKey={app_key}
                    deviceType={device_type}
                    key={key_value}
                    keyLabel={key_value as DIGIT}
                    onDeleteClick={handleDeleteClick(key_value)}
                    viewPathLabel={
                      view_path
                        ? viewPathOptions.find(({ value }) => value === view_path.split('?')[0])?.label ||
                          'No label found, please update'
                        : 'No label, please update'
                    }
                  />
                ))}
              </div>
            </>
          ) : null}
          <h4>New binding</h4>
          {warningBannerOpen ? (
            <Banner className="already-bind-warn-banner" variant="warning">
              Be advised that this binding already exist. Saving it will replace your previous binding
            </Banner>
          ) : null}
          <form
            className="setting-modal__body__content__form"
            id="settings-user-form"
            onSubmit={handleSubmit(handleFormSubmit)}
          >
            <KeyboardButton size="medium" type="ctrl" />
            <div className="setting-modal__body__content__form__inputs">
              <Controller
                control={control}
                name="binding"
                render={({ field }) => (
                  <SelectInput
                    {...field}
                    {...reactSelectMenuPositionDef(MODAL_ID)}
                    defaultValue={null}
                    error={errors?.binding?.value?.message}
                    label="Choose your digit"
                    options={bindingOptions}
                    required
                  />
                )}
              />
              <Controller
                control={control}
                name="view_path"
                render={({ field }) => (
                  <SelectInput
                    {...field}
                    {...reactSelectMenuPositionDef(MODAL_ID)}
                    defaultValue={null}
                    error={errors?.view_path?.value?.message}
                    label="Choose your favourite view"
                    options={viewPathOptions}
                    required
                  />
                )}
              />
              {needAppKeyDeviceType ? (
                <Controller
                  control={control}
                  name="application"
                  render={({ field }) => (
                    <SelectInput
                      {...field}
                      {...reactSelectMenuPositionDef(MODAL_ID)}
                      className="application-select-input"
                      components={{ Option: CustomAppOption }}
                      defaultValue={null}
                      error={errors?.application?.message}
                      label="Choose your most used app"
                      options={applicationOptions}
                      required
                    />
                  )}
                />
              ) : null}
            </div>
          </form>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button form="settings-user-form" type="submit">
          Save
        </Button>
        <Button ghost onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
      <DevTool control={control} />
    </>
  )
}

interface ModalProps extends ModalFormProps {
  isOpen: boolean
}

const SettingModal = ({ isOpen, onClose, viewPathOptions }: ModalProps): ReactElement => {
  return (
    <Modal className="setting-modal" isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>Settings</ModalHeader>
      <SettingForm onClose={onClose} viewPathOptions={viewPathOptions} />
    </Modal>
  )
}
export default SettingModal
